import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { dataQualityApi } from "../../services/dataQualityApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const MODULES = ["EMPLOYEES", "TIMEKEEPING", "PAYROLL", "LEAVE", "MEDICAL", "TRAINING", "RECRUITMENT", "COMPLIANCE"];
const SEVERITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"];
const STATUSES = ["OPEN", "ASSIGNED", "IN_PROGRESS", "WAITING_FOR_EVIDENCE", "REOPENED", "RESOLVED", "ACCEPTED_RISK", "FALSE_POSITIVE"];
const SEVERITY_TEXT = { CRITICAL: "text-red-600", HIGH: "text-orange-600", MEDIUM: "text-amber-600", LOW: "text-gray-500", INFO: "text-slate-500" };
const PAGE_SIZES = [25, 50, 100];

export default function DataQualityPage() {
  const { t } = useLocalization();
  const { canViewDataQuality, canManageDataQuality } = usePermissions();
  const [summary, setSummary] = useState(null);
  const [result, setResult] = useState({ items: [], page: 1, page_size: 50, total_items: 0, total_pages: 1, has_next: false, has_previous: false });
  const [filters, setFilters] = useState({ module: "", severity: "", status: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("severity");
  const [sortDirection, setSortDirection] = useState("asc");
  const [scanning, setScanning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [bulkForm, setBulkForm] = useState({ owner: "", due_date: "", note: "" });
  const [bulkBusy, setBulkBusy] = useState(false);

  const SORT_FIELDS = [
    ["severity", t("mgmtDataQuality.severity")], ["due_date", t("mgmtActionCenter.colDue")], ["last_detected_at", t("mgmtDataQuality.lastDetected")], ["module", t("mgmtDataQuality.module")], ["status", t("common.status")],
  ];

  const load = useCallback(() => {
    dataQualityApi.getSummary().then((r) => setSummary(r.data)).catch(() => {});
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    dataQualityApi.listFindingsPaginated({ ...params, page, page_size: pageSize, sort_by: sortBy, sort_direction: sortDirection })
      .then((r) => setResult(r.data)).catch(() => {});
  }, [filters, page, pageSize, sortBy, sortDirection]);

  useEffect(() => { if (canViewDataQuality) load(); }, [canViewDataQuality, load]);
  useRouteRefresh(load);
  useEffect(() => { setPage(1); setSelected(new Set()); setSelectAllMatching(false); }, [filters, pageSize, sortBy, sortDirection]);

  if (!canViewDataQuality) return <p className="text-sm text-gray-500" data-testid="data-quality-access-denied">{t("common.accessDenied")}</p>;

  const toggleFilter = (key, value) => setFilters((f) => ({ ...f, [key]: f[key] === value ? "" : value }));

  const runScan = async (module) => {
    setScanning(true);
    try {
      const r = await dataQualityApi.runScan(module);
      toast.success(`${t("mgmtDataQuality.scanComplete")}: ${r.data.new_findings} ${t("mgmtLongHourInterview.new")}, ${r.data.reopened_findings} ${t("mgmtDataQuality.reopened")}, ${r.data.auto_resolved_findings} ${t("mgmtDataQuality.autoResolved")}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setScanning(false);
    }
  };

  const exportCsv = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const r = await dataQualityApi.exportFindings(params);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "data_quality_findings.json"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectAllMatching(false);
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectCurrentPage = () => {
    setSelectAllMatching(false);
    setSelected(new Set(result.items.map((f) => f.id)));
  };

  const clearSelection = () => { setSelected(new Set()); setSelectAllMatching(false); };

  const selectAllMatchingFilters = () => { setSelected(new Set()); setSelectAllMatching(true); };

  const selectionCount = selectAllMatching ? result.total_items : selected.size;

  const runBulkAssign = async () => {
    if (!bulkForm.owner || selectionCount === 0) return;
    setBulkBusy(true);
    try {
      const payload = selectAllMatching
        ? { filters: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), owner: bulkForm.owner, due_date: bulkForm.due_date || null, note: bulkForm.note || null }
        : { finding_ids: Array.from(selected), owner: bulkForm.owner, due_date: bulkForm.due_date || null, note: bulkForm.note || null };
      const r = await dataQualityApi.bulkAssign(payload);
      toast.success(`${t("mgmtDataQuality.bulkAssign")}: ${r.data.assigned} ${t("mgmtDataQuality.assigned")}, ${r.data.failed} ${t("mgmtDataQuality.failed")} ${t("mgmtDataQuality.of")} ${r.data.requested} ${t("mgmtDataQuality.requested")}`);
      clearSelection();
      setBulkForm({ owner: "", due_date: "", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  const rangeStart = result.total_items === 0 ? 0 : (result.page - 1) * result.page_size + 1;
  const rangeEnd = Math.min(result.page * result.page_size, result.total_items);

  return (
    <div className="space-y-5" data-testid="data-quality-page">
      <PageHelpBanner pageKey="data-quality" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtDataQuality.help") }} />
      </PageHelpBanner>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtDataQuality.title")}</h2>
          <p className="text-sm text-gray-500">
            {summary?.total_open_findings ?? "-"} {t("mgmtDataQuality.subtitle")}
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-findings-btn" onClick={exportCsv} className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtDataQuality.export")}</button>
          {canManageDataQuality && (
            <button data-testid="run-full-scan-btn" disabled={scanning} onClick={() => runScan(null)} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {scanning ? t("mgmtDataQuality.scanningEllipsis") : t("mgmtDataQuality.runFullScan")}
            </button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {[
            ["CRITICAL", summary.critical], ["HIGH", summary.high], ["MEDIUM", summary.medium], ["LOW", summary.low],
            [t("mgmtDataQuality.reopened"), summary.reopened], [t("mgmtDataQuality.overdue"), summary.overdue], [t("mgmtHealthCommittee.unassigned"), summary.unassigned], [`${t("mgmtDataQuality.resolved")} (${t("mgmtMedical.monthsAbbrev")})`, summary.resolved_this_month],
          ].map(([label, count]) => (
            <button key={label} data-testid={`dq-stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => SEVERITIES.includes(label) && toggleFilter("severity", label)}
              className={`bg-white border rounded-xl p-4 text-left ${filters.severity === label ? "border-blue-400" : "border-gray-200"}`}>
              <p className={`text-2xl font-bold ${SEVERITY_TEXT[label] || "text-gray-700"}`}>{count}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </button>
          ))}
        </div>
      )}

      {summary && (
        <div className="flex gap-2 flex-wrap">
          {MODULES.map((m) => (
            <button key={m} data-testid={`dq-module-card-${m}`} onClick={() => toggleFilter("module", m)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${filters.module === m ? "bg-blue-600 text-white border-blue-600" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
              {m} ({summary.by_module?.[m]?.open ?? 0})
              {canManageDataQuality && (
                <span data-testid={`scan-module-${m}`} onClick={(e) => { e.stopPropagation(); runScan(m); }} className="ml-2 opacity-70 hover:opacity-100" title={`${t("mgmtDataQuality.scan")} ${m} ${t("mgmtDataQuality.only")}`}>⟳</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`dq-status-filter-${s}`} onClick={() => toggleFilter("status", s)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border ${filters.status === s ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{t("mgmtPayroll.sortBy")}</span>
          <select data-testid="dq-sort-by-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs">
            {SORT_FIELDS.map(([field, label]) => <option key={field} value={field}>{label}</option>)}
          </select>
          <button data-testid="dq-sort-direction-btn" onClick={() => setSortDirection((d) => (d === "asc" ? "desc" : "asc"))} className="border border-gray-200 rounded-lg px-2 py-1 text-xs hover:bg-gray-50">
            {sortDirection === "asc" ? `↑ ${t("mgmtDataQuality.asc")}` : `↓ ${t("mgmtDataQuality.desc")}`}
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span data-testid="dq-current-range">{rangeStart}-{rangeEnd} {t("mgmtDataQuality.of")} {result.total_items}</span>
          <select data-testid="dq-page-size-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="border border-gray-200 rounded-lg px-2 py-1 text-xs">
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / {t("mgmtDataQuality.page")}</option>)}
          </select>
          <button data-testid="dq-prev-page-btn" disabled={!result.has_previous} onClick={() => setPage((p) => p - 1)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-40">{t("mgmtDataQuality.prev")}</button>
          <span data-testid="dq-page-indicator">{t("mgmtDataQuality.page")} {result.page} / {result.total_pages}</span>
          <button data-testid="dq-next-page-btn" disabled={!result.has_next} onClick={() => setPage((p) => p + 1)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-40">{t("mgmtDataQuality.next")}</button>
        </div>
      </div>

      {canManageDataQuality && (
        <div className="flex items-center justify-between flex-wrap gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <button data-testid="dq-select-page-btn" onClick={selectCurrentPage} className="font-semibold text-blue-600 hover:underline">{t("mgmtDataQuality.selectPage")} ({result.items.length})</button>
            <button data-testid="dq-select-all-matching-btn" onClick={selectAllMatchingFilters} className="font-semibold text-blue-600 hover:underline">{t("mgmtDataQuality.selectAllMatching")} ({result.total_items})</button>
            {selectionCount > 0 && <button data-testid="dq-clear-selection-btn" onClick={clearSelection} className="text-gray-500 hover:underline">{t("mgmtDataQuality.clear")}</button>}
          </div>
          {selectionCount > 0 && <span data-testid="dq-selection-count" className="font-semibold text-gray-700">{selectionCount} {t("mgmtDataQuality.selected")}</span>}
        </div>
      )}

      {canManageDataQuality && selectionCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-wrap items-end gap-2" data-testid="dq-bulk-assign-panel">
          <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.colOwner")}</label>
            <input data-testid="dq-bulk-owner-input" value={bulkForm.owner} onChange={(e) => setBulkForm((f) => ({ ...f, owner: e.target.value }))} placeholder="owner@example.com" className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtActionCenter.colDue")}</label>
            <input data-testid="dq-bulk-due-date-input" type="date" value={bulkForm.due_date} onChange={(e) => setBulkForm((f) => ({ ...f, due_date: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <div className="flex-1 min-w-[200px]"><label className="block text-xs text-gray-500 mb-1">{t("mgmtDataQuality.sharedNote")}</label>
            <input data-testid="dq-bulk-note-input" value={bulkForm.note} onChange={(e) => setBulkForm((f) => ({ ...f, note: e.target.value }))} placeholder={t("mgmtDataQuality.optionalNoteEveryFinding")} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <button data-testid="dq-bulk-assign-btn" disabled={bulkBusy || !bulkForm.owner} onClick={runBulkAssign} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {bulkBusy ? t("mgmtDataQuality.assigningEllipsis") : `${t("mgmtDataQuality.bulkAssign")} ${selectionCount}`}
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              {canManageDataQuality && <th className="px-4 py-3 w-8"></th>}
              <th className="px-4 py-3 font-medium">{t("mgmtDataQuality.module")}</th><th className="px-4 py-3 font-medium">{t("mgmtDataQuality.type")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtActionCenter.colSeverity")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtHealthCommittee.colOwner")}</th><th className="px-4 py-3 font-medium">{t("mgmtActionCenter.colDue")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colTitle")}</th><th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {result.items.map((f) => (
              <tr key={f.id} className="border-t border-gray-100" data-testid={`dq-finding-row-${f.id}`}>
                {canManageDataQuality && (
                  <td className="px-4 py-2">
                    <input type="checkbox" data-testid={`dq-select-row-${f.id}`} checked={selectAllMatching || selected.has(f.id)} onChange={() => toggleSelectRow(f.id)} />
                  </td>
                )}
                <td className="px-4 py-2 text-gray-600">{f.module}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{f.finding_type}</td>
                <td className="px-4 py-2"><StatusBadge status={f.severity} /></td>
                <td className="px-4 py-2"><StatusBadge status={f.status} /></td>
                <td className="px-4 py-2 text-gray-500 text-xs">{f.owner || "-"}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{f.due_date || "-"}</td>
                <td className="px-4 py-2 text-gray-700">{f.title}</td>
                <td className="px-4 py-2">
                  <Link data-testid={`dq-finding-detail-link-${f.id}`} to={`/management/data-quality/findings/${f.id}`} className="text-xs font-semibold text-blue-600 hover:underline">
                    {t("mgmtLongHourInterview.view")}
                  </Link>
                </td>
              </tr>
            ))}
            {result.items.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-500" data-testid="no-dq-findings-row">{t("mgmtDataQuality.noFindingsFilter")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
